import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoles } from '../../redux/actions/masters/users/role/roleAction';
import { fetchRolePermissions, saveRolePermissions } from '../../redux/actions/masters/users/rolePermission/rolePermissionsActions';
import { fetchPermissions } from '../../redux/actions/masters/users/permission/permissionAction';
import { fetchUsers } from '../../redux/actions/masters/users/user/userAction';
import {
  Box, Card, CardContent, Typography, FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, FormControlLabel, FormGroup, Button, Divider,
  Chip, Paper, Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Layout from '../../components/Layout';

const actions = ['view', 'add', 'edit', 'delete'];

// Mock hierarchical data structure - replace with your actual data
const hierarchicalModules = [
  {
    id: 'user',
    name: 'User Management',
    icon: '👥',
    submodules: [
      { id: 'user_users', name: 'Users', permissions: { can_view: false, can_add: false, can_edit: false, can_delete: false } },
      { id: 'user_role', name: 'Roles', permissions: { can_view: false, can_add: false, can_edit: false, can_delete: false } },
      { id: 'user_permission', name: 'Permissions', permissions: { can_view: false, can_add: false, can_edit: false, can_delete: false } }
    ]
  }
];

const RolePermissionsPage = () => {
  const dispatch = useDispatch();
  const { list: roles } = useSelector(state => state.roles);
  const { list: modules } = useSelector(state => state.permissions);
  const { list: permissions, loading } = useSelector(state => state.rolePermissions);
  const { list: users } = useSelector(state => state.users);

  const [selectedRole, setSelectedRole] = useState('');
  const [localPerms, setLocalPerms] = useState(hierarchicalModules);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
    dispatch(fetchUsers()); // Ensure users are loaded
  }, [dispatch]);

  useEffect(() => {
    if (selectedRole) {
      dispatch(fetchRolePermissions(selectedRole));
    }
  }, [dispatch, selectedRole]);

  useEffect(() => {
    if (modules.length) {
      // Map your existing flat modules to hierarchical structure
      // This is where you'd transform your actual data
      setLocalPerms(hierarchicalModules);
    }
  }, [permissions, modules]);

  useEffect(() => {
    setSelectedUser(''); // Reset username when role changes
  }, [selectedRole]);

  const handleCheckbox = (moduleId, submoduleId, action) => (e) => {
    setLocalPerms(perms =>
      perms.map(module => {
        if (module.id === moduleId) {
          return {
            ...module,
            submodules: module.submodules.map(sub => {
              if (sub.id === submoduleId) {
                return {
                  ...sub,
                  permissions: {
                    ...sub.permissions,
                    [`can_${action}`]: e.target.checked
                  }
                };
              }
              return sub;
            })
          };
        }
        return module;
      })
    );

    // Auto-close accordion after permission change
    setTimeout(() => {
      setExpandedModules(prev => {
        const newSet = new Set(prev);
        newSet.delete(moduleId);
        return newSet;
      });
    }, 1000); // Close after 1 second
  };

  const handleModuleToggle = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const validatePermissions = () => {
    // Check if any permissions are selected
    const hasPermissions = localPerms.some(module => 
      module.submodules.some(sub => 
        Object.values(sub.permissions).some(Boolean)
      )
    );

    if (!hasPermissions) {
      return 'Please select at least one permission before saving.';
    }

    return null;
  };

  const handleSave = async () => {
    try {
      // Clear previous messages
      setSaveMessage('');
      setErrorMessage('');
      setIsSaving(true);

      // Validate permissions
      const validationError = validatePermissions();
      if (validationError) {
        setErrorMessage(validationError);
        setIsSaving(false);
        return;
      }

      // Check if role is selected
      if (!selectedRole) {
        setErrorMessage('Please select a role before saving permissions.');
        setIsSaving(false);
        return;
      }

      // Transform hierarchical data back to flat structure for API
      const flatPermissions = localPerms.flatMap(module => 
        module.submodules.map(sub => ({
          module_id: sub.id,
          module_name: sub.name,
          ...sub.permissions
        }))
      );

      // Validate transformed data
      if (!flatPermissions || flatPermissions.length === 0) {
        setErrorMessage('No permissions data to save. Please try again.');
        setIsSaving(false);
        return;
      }

      // Attempt to save permissions
      const result = await dispatch(saveRolePermissions({ 
        roleId: selectedRole, 
        permissions: flatPermissions 
      }));

      // Check if the save was successful
      if (result && result.error) {
        throw new Error(result.error);
      }

      // Success
      setSaveMessage('Permissions saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);

    } catch (error) {
      console.error('Error saving permissions:', error);
      
      // Handle different types of errors
      let errorMsg = 'Failed to save permissions. Please try again.';
      
      if (error.message) {
        if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMsg = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('unauthorized') || error.message.includes('401')) {
          errorMsg = 'Session expired. Please log in again.';
        } else if (error.message.includes('forbidden') || error.message.includes('403')) {
          errorMsg = 'You do not have permission to save role permissions.';
        } else if (error.message.includes('server') || error.message.includes('500')) {
          errorMsg = 'Server error. Please try again later.';
        } else {
          errorMsg = error.message;
        }
      }
      
      setErrorMessage(errorMsg);
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);

    } finally {
      setIsSaving(false);
    }
  };

  const getSelectedRoleName = () => {
    const role = roles.find(r => r.role_id === selectedRole);
    return role ? role.role_name : '';
  };

  const getPermissionCount = () => {
    return localPerms.reduce((moduleCount, module) => {
      return moduleCount + module.submodules.reduce((subCount, sub) => {
        return subCount + Object.values(sub.permissions).filter(Boolean).length;
      }, 0);
    }, 0);
  };

  const usersForRole = users.filter(u => u.role_id === selectedRole);

  return (
    <Layout>
      <Box sx={{ 
        maxWidth: 700, 
        mx: 'auto', 
        mt: 3,
        mb: 3,
        px: { xs: 2, sm: 3 }
      }}>
        {/* Header Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 3,
          p: 2,
          background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
          borderRadius: 2,
          border: '1px solid #A5D6A7',
          boxShadow: '0 2px 12px rgba(0, 200, 83, 0.1)'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: 1 
          }}>
            <Box sx={{
              background: '#00C853',
              borderRadius: '50%',
              p: 1,
              mr: 1.5,
              boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)'
            }}>
              <SecurityIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Typography variant="h5" sx={{ 
              color: '#1B5E20', 
              fontWeight: 800, 
              fontSize: { xs: 20, sm: 22 },
              letterSpacing: 1 
            }}>
              Role Permissions
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ 
            color: '#2E7D32', 
            fontSize: 14,
            opacity: 0.8 
          }}>
            Manage access permissions for different roles in your system
          </Typography>
        </Box>

        {/* Main Card */}
        <Card elevation={0} sx={{
          background: '#fff',
          border: '1.5px solid #A5D6A7',
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(0, 200, 83, 0.08)',
          overflow: 'hidden',
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Role Selection Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ 
                color: '#1B5E20', 
                fontWeight: 700, 
                mb: 1.5,
                fontSize: 16 
              }}>
                Select Role
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="role-select-label" sx={{ 
                  color: '#1B5E20', 
                  fontWeight: 600, 
                  fontSize: 14 
                }}>
                  Choose a role to manage permissions
                </InputLabel>
                <Select
                  labelId="role-select-label"
                  value={selectedRole}
                  label="Choose a role to manage permissions"
                  onChange={e => setSelectedRole(e.target.value)}
                  sx={{
                    background: '#fff',
                    borderRadius: 2,
                    border: '1.5px solid #C8E6C9',
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#1B5E20',
                    '& .MuiSelect-icon': { color: '#00C853' },
                    '&:hover': {
                      border: '1.5px solid #00C853',
                      boxShadow: '0 2px 8px rgba(0, 200, 83, 0.15)',
                    },
                    '&.Mui-focused': {
                      border: '1.5px solid #00C853',
                      boxShadow: '0 0 0 3px rgba(0, 200, 83, 0.1)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <MenuItem value="" disabled>Select a role</MenuItem>
                  {roles.map(role => (
                    <MenuItem key={role.role_id} value={role.role_id} sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      '&:hover': {
                        background: '#E8F5E9',
                      },
                    }}>
                      {role.role_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Username Dropdown (for viewing) */}
              {selectedRole && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel id="user-select-label" sx={{ color: '#1B5E20', fontWeight: 600, fontSize: 14 }}>
                      Select Username (for viewing)
                    </InputLabel>
                    <Select
                      labelId="user-select-label"
                      value={selectedUser}
                      label="Select Username (for viewing)"
                      onChange={e => setSelectedUser(e.target.value)}
                      sx={{
                        background: '#fff',
                        borderRadius: 2,
                        border: '1.5px solid #C8E6C9',
                        fontWeight: 600,
                        fontSize: 14,
                        color: '#1B5E20',
                        mt: 1,
                      }}
                    >
                      <MenuItem value="" disabled>Select a user</MenuItem>
                      {usersForRole.map(user => (
                        <MenuItem key={user.user_id} value={user.user_id}>
                          {user.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>

            {/* Selected Role Info */}
            {selectedRole && (
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                borderRadius: 2,
                border: '1px solid #A5D6A7'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#1B5E20', 
                    fontWeight: 700,
                    fontSize: 16 
                  }}>
                    Managing permissions for: <strong>{getSelectedRoleName()}</strong>
                  </Typography>
                  <Chip 
                    label={`${getPermissionCount()} permissions selected`}
                    sx={{
                      background: '#00C853',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ 
                  color: '#2E7D32',
                  opacity: 0.8,
                  fontSize: 13
                }}>
                  Expand modules to see their submodules and manage permissions. Changes will be saved when you click the save button.
                </Typography>
              </Box>
            )}

            {/* Hierarchical Permissions Section */}
            {selectedRole && (
              <Box>
                <Typography variant="h6" sx={{ 
                  color: '#1B5E20', 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: 16 
                }}>
                  Module Permissions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {localPerms.map((module) => (
                    <Paper key={module.id} elevation={0} sx={{
                      borderRadius: 2,
                      border: '1.5px solid #C8E6C9',
                      background: '#fff',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        border: '1.5px solid #00C853',
                        boxShadow: '0 4px 12px rgba(0, 200, 83, 0.12)',
                      },
                    }}>
                      {/* Main Module Header */}
                      <Box
                        onClick={() => handleModuleToggle(module.id)}
                        sx={{
                          background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                          borderBottom: '1px solid #A5D6A7',
                          p: 2,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #C8E6C9 0%, #A5D6A7 100%)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <Typography sx={{ fontSize: 20, mr: 1.5 }}>
                            {module.icon}
                          </Typography>
                          <Typography sx={{ 
                            fontWeight: 700, 
                            color: '#1B5E20', 
                            fontSize: 16,
                          }}>
                            {module.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Chip 
                            label={`${module.submodules.length} submodules`}
                            size="small"
                            sx={{
                              background: '#00C853',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: 10,
                              height: 20,
                            }}
                          />
                          <ExpandMoreIcon 
                            sx={{ 
                              color: '#00C853', 
                              fontSize: 24,
                              transform: expandedModules.has(module.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease',
                            }} 
                          />
                        </Box>
                      </Box>

                      {/* Submodules */}
                      <Collapse in={expandedModules.has(module.id)}>
                        <Box sx={{ p: 0 }}>
                          {module.submodules.map((submodule, idx) => (
                            <Box key={submodule.id} sx={{
                              borderBottom: idx < module.submodules.length - 1 ? '1px solid #E8F5E9' : 'none',
                              p: 2,
                              background: '#fff',
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ 
                                  fontWeight: 600, 
                                  color: '#1B5E20', 
                                  fontSize: 14,
                                }}>
                                  {submodule.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {actions.map(action => (
                                    <Chip
                                      key={action}
                                      label={action.toUpperCase()}
                                      size="small"
                                      sx={{
                                        background: submodule.permissions[`can_${action}`] ? '#00C853' : '#E8F5E9',
                                        color: submodule.permissions[`can_${action}`] ? '#fff' : '#1B5E20',
                                        fontWeight: 600,
                                        fontSize: 8,
                                        height: 16,
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                              
                              <Typography variant="body2" sx={{ 
                                color: '#2E7D32', 
                                mb: 2,
                                fontWeight: 500,
                                fontSize: 12
                              }}>
                                Select the permissions for this submodule:
                              </Typography>
                              
                              <FormGroup row sx={{ gap: 2 }}>
                                {actions.map(action => (
                                  <FormControlLabel
                                    key={action}
                                    control={
                                      <Checkbox
                                        checked={!!submodule.permissions[`can_${action}`]}
                                        onChange={handleCheckbox(module.id, submodule.id, action)}
                                        sx={{
                                          color: '#00C853',
                                          '&.Mui-checked': { 
                                            color: '#00C853',
                                            transform: 'scale(1.05)',
                                          },
                                          '&:hover': { 
                                            backgroundColor: 'rgba(0, 200, 83, 0.08)',
                                            transform: 'scale(1.02)',
                                          },
                                          borderRadius: 1,
                                          transition: 'all 0.2s ease',
                                        }}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ 
                                          fontWeight: 600, 
                                          color: '#1B5E20', 
                                          fontSize: 12,
                                          textTransform: 'capitalize'
                                        }}>
                                          {action}
                                        </Typography>
                                        <Chip
                                          label={action.toUpperCase()}
                                          size="small"
                                          sx={{
                                            background: submodule.permissions[`can_${action}`] ? '#00C853' : '#E8F5E9',
                                            color: submodule.permissions[`can_${action}`] ? '#fff' : '#1B5E20',
                                            fontWeight: 600,
                                            fontSize: 7,
                                            height: 14,
                                          }}
                                        />
                                      </Box>
                                    }
                                    sx={{ 
                                      m: 0,
                                      p: 1.5,
                                      borderRadius: 1.5,
                                      border: '1px solid #E8F5E9',
                                      background: submodule.permissions[`can_${action}`] ? '#E8F5E9' : 'transparent',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        background: '#F1F8E9',
                                        border: '1px solid #A5D6A7',
                                      },
                                    }}
                                  />
                                ))}
                              </FormGroup>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {/* Save Button and Messages */}
            {selectedRole && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="medium"
                  sx={{
                    background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 16,
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    boxShadow: '0 2px 12px rgba(0, 200, 83, 0.3)',
                    letterSpacing: 0.5,
                    transition: 'all 0.3s ease',
                    minWidth: 160,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
                      boxShadow: '0 4px 16px rgba(0, 200, 83, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: '#C8E6C9',
                      color: '#A5D6A7',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                  }}
                  onClick={handleSave}
                  disabled={!selectedRole || loading || isSaving}
                >
                  {isSaving ? 'Saving...' : loading ? 'Loading...' : 'Save Permissions'}
                </Button>
                
                {/* Success Message */}
                {saveMessage && (
                  <Typography variant="body2" sx={{ 
                    color: '#00C853', 
                    mt: 1.5,
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5
                  }}>
                    ✓ {saveMessage}
                  </Typography>
                )}
                
                {/* Error Message */}
                {errorMessage && (
                  <Typography variant="body2" sx={{ 
                    color: '#D32F2F', 
                    mt: 1.5,
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    textAlign: 'center',
                    maxWidth: 400,
                    mx: 'auto'
                  }}>
                    ⚠ {errorMessage}
                  </Typography>
                )}
                
                {loading && !isSaving && (
                  <Typography variant="body2" sx={{ 
                    color: '#2E7D32', 
                    mt: 1.5,
                    opacity: 0.8,
                    fontSize: 12
                  }}>
                    Please wait while we save your changes...
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default RolePermissionsPage; 